import { useState, useEffect } from "react"
import { PrismButton, PrismCheckbox, PrismDialog } from "@prism-ui/react"
import {
    APPLICANT_TYPE_MENTEE,
    APPLICANT_TYPE_MENTOR,
    APPLICANT_TYPE_COMMITTEE,
} from "../constants"
import "./TimeCommitmentModal.css"

interface Props {
    show: boolean
    applicationType: number | string
    onCancel: () => void
    handleSubmit: () => void
    showOkButton: boolean
}

interface Acknowledgement {
    statement: string
    detail: string
}

interface TrackContent {
    intro: string
    items: Acknowledgement[]
}

const PROGRAM_DURATION: Acknowledgement = {
    statement: "I can commit to the full program year.",
    detail: "The program runs ~9 months (February – October).",
}

const trackContent: Record<number, TrackContent> = {
    [APPLICANT_TYPE_MENTEE]: {
        intro:
            "As a Mentee, the CLEAR program is a meaningful investment of your time alongside your day job. " +
            "Before applying, please consider whether the timing works for you this year.",
        items: [
            {
                statement: "The timing works for me this year.",
                detail:
                    "Life happens — the program is designed with flexibility for the unexpected. However, " +
                    "if you already know you'll have extended time off, a major project crunch, or other " +
                    "competing priorities during the program year, it may be better to apply in a future cycle.",
            },
            {
                statement: "I understand seats are limited.",
                detail:
                    "Each chapter has a fixed number of mentee seats that cannot be backfilled once the " +
                    "program begins. If you accept a seat and later withdraw, that opportunity is lost for the year.",
            },
            PROGRAM_DURATION,
            {
                statement: "I will attend the monthly sessions.",
                detail:
                    "~10 hours/month of classroom and workshop time; one unexcused absence is permitted.",
            },
            {
                statement: "I will make time for project work.",
                detail: "1–8 hours/week throughout the program (varies by phase and scope).",
            },
        ],
    },
    [APPLICANT_TYPE_MENTOR]: {
        intro:
            "As a Mentor, you'll be paired with a Mentee and a Committee Member to form a triad. " +
            "Your guidance and experience are a key part of your Mentee's success in the program.",
        items: [
            {
                statement: "I'm available for the full program year.",
                detail:
                    "While Mentor vacancies can be backfilled if needed, a mid-year transition disrupts " +
                    "the triad relationship.",
            },
            PROGRAM_DURATION,
            {
                statement: "I will attend the monthly sessions if I'm new to CLEAR.",
                detail:
                    "~10 hours/month of classroom and workshop time is required for new-to-CLEAR mentors " +
                    "(optional for tenured mentors).",
            },
            {
                statement: "I will stay engaged with my triad.",
                detail: "Regular check-ins with your Mentee and Committee Member throughout the program.",
            },
        ],
    },
    [APPLICANT_TYPE_COMMITTEE]: {
        intro:
            "As a Committee Member, you'll be the third member of a triad — supporting your Mentor and " +
            "Mentee and reporting progress back to the Committee. This role is ideal for program graduates " +
            "who want to stay involved and give back but may not meet Mentor title requirements, as well as " +
            "people leaders who are passionate about mentoring but can't serve as a formal Mentor in the " +
            "program. You'll have full access to the classroom sessions and knowledge learnings throughout " +
            "the year — without the commitment of a capstone project.",
        items: [
            {
                statement: "I will be consistently available this year.",
                detail:
                    "Committee vacancies can be backfilled if needed, so the commitment is more flexible. " +
                    "That said, consistency matters.",
            },
            PROGRAM_DURATION,
            {
                statement: "I understand classroom attendance is optional.",
                detail: "There's no attendance requirement, but you're welcome to participate in any session.",
            },
            {
                statement: "I will check in with my triad and report back.",
                detail: "Periodic check-ins with your Mentor and Mentee, and reporting back to the Committee.",
            },
        ],
    },
}

export const TimeCommitmentModal = ({ show, onCancel, applicationType, handleSubmit, showOkButton }: Props) => {
    // Statements must be acknowledged in order, so progress is just "how many in a row".
    const [acknowledged, setAcknowledged] = useState(0)

    useEffect(() => {
        if (!show) setAcknowledged(0)
    }, [show])

    const typeKey = typeof applicationType === "string" ? Number(applicationType) : applicationType
    const content = trackContent[typeKey] || trackContent[APPLICANT_TYPE_MENTEE]
    const total = content.items.length

    useEffect(() => {
        setAcknowledged(0)
    }, [typeKey])

    // Checking a statement unlocks the next; unchecking one re-locks everything after it.
    // Prism 9 echoes prismChange when React sets `checked`; those echoes leave the count unchanged.
    const onItemChange = (index: number) => (e: CustomEvent<{ checked?: boolean }>) => {
        const checked = e?.detail?.checked
        setAcknowledged((prev) => {
            const isChecked = index < prev
            const next = typeof checked === "boolean" ? checked : !isChecked
            if (next === isChecked) return prev
            if (next) return index === prev ? prev + 1 : prev
            return index
        })
    }

    const allAcknowledged = acknowledged >= total

    return (
        <PrismDialog open={show} setAriaLabel="Time Commitments" close-method="user" onPrismClose={onCancel}>
            <span slot="header">Time Commitments</span>
            <div className="tc-modal">
                <p className="tc-intro">{content.intro}</p>
                {!showOkButton && (
                    <p className="tc-progress" aria-live="polite">
                        Check each statement in order to continue — {Math.min(acknowledged, total)} of {total} acknowledged
                    </p>
                )}
                <ol className="tc-list">
                    {content.items.map((item, index) => {
                        const state = index < acknowledged ? "done" : index === acknowledged ? "current" : "locked"
                        return (
                            <li key={item.statement} className={`tc-item tc-item--${showOkButton ? "readonly" : state}`}>
                                {showOkButton ? (
                                    <>
                                        <strong>{item.statement}</strong>
                                        <p>{item.detail}</p>
                                    </>
                                ) : (
                                    <>
                                        <PrismCheckbox
                                            name={`tc-item-${index + 1}`}
                                            label={item.statement}
                                            setAriaDescribedby={`tc-item-${index + 1}-detail`}
                                            checked={index < acknowledged}
                                            disabled={index > acknowledged}
                                            onPrismChange={onItemChange(index)}
                                        />
                                        <p id={`tc-item-${index + 1}-detail`} className="tc-item__detail">
                                            {item.detail}
                                        </p>
                                    </>
                                )}
                            </li>
                        )
                    })}
                </ol>
            </div>
                <div slot="footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                    <div style={{ display: showOkButton ? undefined : 'none' }}>
                        <PrismButton tone="primary" onPrismClick={onCancel}>OK</PrismButton>
                    </div>
                    <div style={{ display: showOkButton ? 'none' : 'flex', gap: '0.5rem' }}>
                        <PrismButton
                            tone="primary"
                            onPrismClick={handleSubmit}
                            disabled={!allAcknowledged}
                        >
                            Accept and Proceed
                        </PrismButton>
                        <PrismButton tone="secondary" onPrismClick={onCancel}>
                            Cancel
                        </PrismButton>
                    </div>
                </div>
        </PrismDialog>
    )
}

